import {Button, Spinner, css, view} from '@sail/ui/deprecated';
import {animate} from '@sail/engine/deprecated';
import {AnimatePresence} from '@sail/engine';
import {
  defineMessages,
  FormattedMessage,
  MessageDescriptor,
  useIntl,
} from 'react-intl';

import {
  Query,
  SearchableSelect,
  SearchableSelectEmpty,
  SearchableSelectResultProps,
  useSearch,
} from '@stripe-internal/sail/Search';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {ThemeProvider, useTheme} from '../../ThemeProvider';
import {useNewUniversalHelpDesign} from '../../FeatureFlagsProvider';
import {ANIMATION_DURATION} from '../../animation';

const messages = defineMessages({
  defaultLabel: {
    id: 'conversations_ui.chat.row.selectOptions.defaultLabel',
    description: 'Label for the option selector in the select options message',
    defaultMessage: 'Select an option…',
  },
  inputPlaceholder: {
    id: 'conversations_ui.chat.row.selectOptions.search.inputPlaceholder',
    description: 'Placeholder text shown in option selector search box',
    defaultMessage: 'Search…',
  },
  noResults: {
    id: 'conversations_ui.chat.row.selectOptions.search.noResultsFound',
    description:
      "Message shown in option selector when the user's search resulted in no matches.",
    defaultMessage: 'No results found',
  },
  searchError: {
    id: 'conversations_ui.chat.row.selectOptions.search.error',
    description: 'Message shown in option selector when there is an error.',
    defaultMessage: 'There was an error retrieving the results.',
  },
  submit: {
    id: 'conversations_ui.conversation_ui.chat.row.selectOptions.submit.label',
    description: 'Label for the submit button for the option selector.',
    defaultMessage: 'Submit',
  },
});

export interface Option {
  value: string;
  label: string;
}

const searchableTextFromOption = (option: Option): string =>
  option.label.toLowerCase();

const optionQuery = (options: Option[], queryString: string): Option[] => {
  if (!queryString || queryString.length === 0) {
    return options;
  } else {
    const lowerQuery = queryString.toLowerCase();
    return options.filter(
      (o) => searchableTextFromOption(o).indexOf(lowerQuery) >= 0,
    );
  }
};

const createQuery = (options: Option[]): Query<Option> => {
  return (queryString: string) =>
    Promise.resolve(optionQuery(options, queryString));
};

const renderOption = (
  props: SearchableSelectResultProps<Option>,
): JSX.Element => <>{props.entry.label}</>;

const buttonLabelForOption = (option: Option): string => option.label;
const formValueForOption = (option: Option): string => option.value;

const renderEmpty = (): JSX.Element => (
  <SearchableSelectEmpty>
    <FormattedMessage {...messages.noResults} />
  </SearchableSelectEmpty>
);

const renderError = (): JSX.Element => (
  <ThemeProvider>
    <view.div css={{padding: css.value('8px')}}>
      <view.span css={{color: 'critical'}}>
        <FormattedMessage {...messages.searchError} />
      </view.span>
    </view.div>
  </ThemeProvider>
);

export type Props = {
  label?: MessageDescriptor;
  options: Option[];
  onSelected: (option: Option) => void;
  selectedOptionId?: string;
  defaultOptionId?: string;
  error?: React.ReactNode;
  disabled?: boolean;
  submitting?: boolean;
};

export const ChatSelectOptions = ({
  label,
  options,
  onSelected,
  selectedOptionId,
  defaultOptionId,
  error,
  disabled,
  submitting,
}: Props): JSX.Element => {
  const useNewDesign = useNewUniversalHelpDesign();

  const intl = useIntl();
  const theme = useTheme();
  const [selectedOption, setSelectedOption] = useState<
    Option | null | undefined
  >(
    options.find(
      (o) => o.value === selectedOptionId || o.value === defaultOptionId,
    ),
  );

  useEffect(() => {
    if (selectedOptionId && !selectedOption) {
      setSelectedOption(options.find((o) => o.value === selectedOptionId));
    }
  }, [selectedOptionId, selectedOption, options]);

  const onChange = useCallback((option?: Option | null) => {
    setSelectedOption(option);
  }, []);

  const onSubmit = useCallback(() => {
    if (selectedOption && !disabled && !submitting) {
      onSelected(selectedOption);
    }
  }, [onSelected, selectedOption, disabled, submitting]);

  const query = React.useMemo(() => createQuery(options), [options]);

  const searchProps = useSearch(query);

  const alreadySelected = !!selectedOptionId && !!selectedOption;

  return (
    <view.div
      data-testid="chat-options-select-container"
      css={{
        display: css.value('flex'),
        flexDirection: css.value('column'),
        rowGap: css.value('12px'),
        // In the new designs, we only want the top padding
        padding: useNewDesign
          ? css.value('10px 0px 0px 0px')
          : css.value('10px 0px'),
        width: css.value('100%'),
      }}
    >
      <SearchableSelect
        {...searchProps}
        name="options-select"
        data-testid="chat-options-select"
        chooseLabel={intl.formatMessage(label || messages.defaultLabel)}
        inputPlaceholder={intl.formatMessage(messages.inputPlaceholder)}
        getSearchableText={searchableTextFromOption}
        autoflip
        onChange={onChange}
        value={selectedOption}
        width="maximized"
        size="large"
        renderItem={renderOption}
        renderEmpty={renderEmpty}
        renderError={renderError}
        getButtonLabel={buttonLabelForOption}
        getFormValue={formValueForOption}
        disabled={alreadySelected}
      />
      <AnimatePresence>
        {alreadySelected ? null : (
          <Button
            size="medium"
            data-testid="chat-options-select-submit"
            type="primary"
            onPress={onSubmit}
            disabled={
              disabled || submitting || alreadySelected || !selectedOption
            }
            css={{
              width: css.value('100%'),
              color: css.value(theme.onCtaTextColor),
              backgroundColor: css.value(theme.ctaColor),
              borderColor: css.value(theme.ctaColor),
              outlineColor: css.value(theme.ctaColor),
              '--button-background-color': css.value(theme.ctaColor),
              '--keyline': css.value(theme.ctaColor),
              keyline: css.value(theme.ctaColor),
              '&:hover': {
                backgroundColor: css.value(theme.ctaHoverColor),
              },
              '&:focus': {
                outline: theme.focusVisibleOutline,
                focusRing: theme.focusVisibleOutline
                  ? css.value('none')
                  : undefined,
              },
            }}
            uses={[
              animate.out({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'ease-in',
              }),
            ]}
          >
            {submitting ? (
              <Spinner data-testid="options-spinner" />
            ) : (
              <FormattedMessage {...messages.submit} />
            )}
          </Button>
        )}
      </AnimatePresence>
      {error}
    </view.div>
  );
};
